<template>
  <section class="business-structure">
    <div class="container">
      <!-- 标题区 -->
      <div class="section-header">
        <span class="section-label">BUSINESS STRUCTURE</span>
        <h2 class="section-title">商业模式与发展结构</h2>
        <p class="section-subtitle">从能力基础设施 → 协同生态 → 长期技术布局</p>
      </div>

      <!-- 三阶段横向结构 -->
      <div class="strategy-container">
        <div class="stage-card" v-for="(stage, index) in stages" :key="index">
          <!-- 阶段标题 -->
          <div class="card-header">
            <span class="stage-badge">{{ stage.number }}</span>
            <h3 class="stage-title">{{ stage.title }}</h3>
          </div>

          <!-- 战略定义 -->
          <p class="strategy-definition">{{ stage.summary }}</p>

          <!-- 建设重点 -->
          <div class="section-block">
            <h4 class="block-title">建设重点</h4>
            <ul class="block-list">
              <li v-for="(point, i) in stage.capabilities.slice(0, 2)" :key="`point-${i}`">
                <span class="list-dot">→</span>{{ point }}
              </li>
            </ul>
          </div>

          <!-- 技术能力 -->
          <div class="section-block">
            <h4 class="block-title">技术能力</h4>
            <ul class="block-list">
              <li v-for="(tech, i) in stage.technologies.slice(0, 2)" :key="`tech-${i}`">
                <span class="list-dot">→</span>{{ tech }}
              </li>
            </ul>
          </div>

          <!-- 商业价值 -->
          <div class="section-block section-block-last">
            <h4 class="block-title">商业价值</h4>
            <p class="block-text">{{ stage.value }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'BusinessStructure',
  data() {
    return {
      stages: [
        {
          number: '01',
          title: '平台基础设施',
          summary: '建立能力验证与存证体系，构建教育-企业-学生三方协同框架',
          capabilities: [
            '智能任务匹配与能力评估引擎',
            '区块链分布式存证与能力档案'
          ],
          technologies: [
            'AI 驱动的能力识别系统',
            '区块链存证技术'
          ],
          value: '打破教育与就业信息不对称，让学生的真实能力成为其最有价值的资产'
        },
        {
          number: '02',
          title: '协同生态',
          summary: '整合教育、企业、资本等多方参与者，构建正和博弈系统',
          capabilities: [
            '多方参与者激励机制与利益分配',
            '人才能力价值量化与交易体系'
          ],
          technologies: [
            '智能合约自动结算',
            '动态激励机制'
          ],
          value: '实现教育创造就业、企业获得人才、资本方获得回报的正循环生态'
        },
        {
          number: '03',
          title: '长期技术布局',
          summary: '扩展能力验证体系到生物科技等前沿领域，支持人类科技进步',
          capabilities: [
            '生物科技领域专业能力验证体系',
            '研究成果溯源与知识产权管理'
          ],
          technologies: [
            '领域知识图谱',
            '专业能力模型'
          ],
          value: '成为支撑人类科技进步的核心平台，与国家科技战略深度融合'
        }
      ]
    }
  }
}
</script>

<style scoped>
.business-structure {
  padding: 6rem 2rem;
  background: #070B14;
  position: relative;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

/* 极弱网格纹理 */
.business-structure::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  pointer-events: none;
  z-index: 0;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

/* 标题区 */
.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-label {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: #00B9FF;
  text-transform: uppercase;
  margin-bottom: 1.2rem;
  opacity: 0.7;
}

.section-title {
  font-size: 2.8rem;
  font-weight: 700;
  color: #F9FAFB;
  margin: 0 0 1rem 0;
  line-height: 1.2;
}

.section-subtitle {
  font-size: 1rem;
  color: rgba(209, 213, 219, 0.75);
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.7;
}

/* 三卡片横向布局 */
.strategy-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
}

.stage-card {
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 2rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stage-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 100% 0%, rgba(0, 185, 255, 0.05), transparent 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.stage-card:hover {
  transform: translateY(-4px);
  background: rgba(15, 23, 42, 0.6);
  border-color: rgba(0, 185, 255, 0.15);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.stage-card:hover::before {
  opacity: 1;
}

/* 卡片头部 */
.card-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stage-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(0, 185, 255, 0.08);
  border: 1px solid rgba(0, 185, 255, 0.15);
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #00B9FF;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.stage-card:hover .stage-badge {
  background: rgba(0, 185, 255, 0.12);
  border-color: rgba(0, 185, 255, 0.25);
}

.stage-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #F9FAFB;
  margin: 0;
  line-height: 1.4;
}

/* 战略定义 */
.strategy-definition {
  font-size: 0.95rem;
  color: rgba(209, 213, 219, 0.8);
  line-height: 1.7;
  margin: 0 0 1.75rem 0;
  padding-bottom: 1.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

/* 内容分块 */
.section-block {
  margin-bottom: 1.5rem;
}

.section-block-last {
  margin-bottom: 0;
}

.block-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #00B9FF;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 0.75rem 0;
  opacity: 0.85;
}

.block-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.block-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.9rem;
  color: rgba(209, 213, 219, 0.8);
  line-height: 1.6;
}

.list-dot {
  color: #00B9FF;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}

.block-text {
  font-size: 0.9rem;
  color: rgba(209, 213, 219, 0.8);
  line-height: 1.7;
  margin: 0;
}

/* 响应式布局 */
@media (max-width: 1024px) {
  .section-title {
    font-size: 2.3rem;
  }

  .strategy-container {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }

  .stage-card {
    padding: 1.75rem;
  }

  .stage-title {
    font-size: 1.2rem;
  }
}

@media (max-width: 768px) {
  .business-structure {
    padding: 4rem 1.5rem;
  }

  .section-header {
    margin-bottom: 3rem;
  }

  .section-title {
    font-size: 1.9rem;
  }

  .section-subtitle {
    font-size: 0.95rem;
  }

  .strategy-container {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }

  .stage-card {
    padding: 1.5rem;
  }

  .card-header {
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }

  .stage-badge {
    width: 40px;
    height: 40px;
    font-size: 0.85rem;
  }

  .stage-title {
    font-size: 1.1rem;
  }

  .strategy-definition {
    font-size: 0.9rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1.25rem;
  }

  .section-block {
    margin-bottom: 1.25rem;
  }

  .block-title {
    font-size: 0.75rem;
    margin-bottom: 0.6rem;
  }

  .block-list li {
    font-size: 0.85rem;
    gap: 0.5rem;
  }

  .block-text {
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .business-structure {
    padding: 3rem 1rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .section-subtitle {
    font-size: 0.9rem;
  }

  .stage-card {
    padding: 1.25rem;
  }
}
</style>

